import React, { useState, useEffect } from 'react'
import { PageHeader, Button, Table, Skeleton, Popconfirm, Modal } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

import ResultError from '@packages/ResultError'
import { MANUFACTURERS_ICON } from '@constants/IconsConstants'
import { useColumnFilterProps } from '@utils/columnFilter'
import { getManufacturersQuery } from '@graphql/queries'
import { useUpdateManufacturer, useDeleteManufacturer } from '@graphql/mutations'

import EditableCell from '../../EditableCell'
import ManageManufacturerForm from '../ManageManufacturerForm'

const tableProps = {
  size: 'middle',
  pagination: false,
}
const newButtonProps = {
  type: 'primary',
  icon: <PlusOutlined />,
  key: 'newManufacturer',
}

function ManageManufacturerTable() {
  const [manufacturersData, setManufacturersData] = useState(false)
  const [formModalVisible, setFormModalVisible] = useState(false)
  const { error, data } = useQuery(getManufacturersQuery)
  const [updateManufacturer] = useUpdateManufacturer()
  const [deleteManufacturer] = useDeleteManufacturer()
  const useFilterField = field => useColumnFilterProps(field)
  const toggleFormModal = () => setFormModalVisible(!formModalVisible)

  useEffect(() => {
    if (data) setManufacturersData(data.manufacturers)
  }, [data])

  const handleSave = data => {
    const { id, name } = data
    const newData = [...manufacturersData]
    const index = newData.findIndex(item => item.id === data.id)
    const item = newData[index]

    newData.splice(index, 1, { ...item, ...data })

    if (JSON.stringify(newData) !== JSON.stringify(manufacturersData)) {
      updateManufacturer({
        variables: {
          id,
          data: {
            name: { set: name },
          },
        },
      })

      setManufacturersData(newData)
    }
  }

  const handleDelete = id => {
    deleteManufacturer({
      variables: { id },
      refetchQueries: ['GetManufacturersQuery'],
    })
  }
  const columns = [
    {
      dataIndex: 'name',
      title: 'Nome',
      editable: true,
      ...useFilterField('name'),
    },
    {
      align: 'center',
      render: (value, { id, name }) => (
        <Popconfirm
          title={`Tem a certeza que quer eliminar ${name} ?`}
          onConfirm={() => handleDelete(id)}
          okText="Sim"
          cancelText="Não"
        >
          <DeleteOutlined />
        </Popconfirm>
      ),
    },
  ]
  const mappedColumns = columns.map(col => {
    if (!col.editable) return col

    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    }
  })

  return (
    <>
      <Modal
        title="Novo fabricante"
        visible={formModalVisible}
        onCancel={() => toggleFormModal()}
        footer={null}
      >
        <ManageManufacturerForm afterSubmit={() => toggleFormModal()} />
      </Modal>

      <PageHeader
        title="Fabricantes"
        avatar={{ src: MANUFACTURERS_ICON }}
        extra={[
          <Button {...newButtonProps} onClick={() => toggleFormModal()}>
            Novo fabricante
          </Button>,
        ]}
      />

      {error && <ResultError />}

      <Skeleton loading={!error && !manufacturersData} active>
        {manufacturersData && (
          <Table
            components={EditableCell.Components}
            columns={mappedColumns}
            dataSource={manufacturersData}
            rowKey={({ id }) => id}
            rowClassName={() => 'editable-row'}
            {...tableProps}
          />
        )}
      </Skeleton>
    </>
  )
}

export default ManageManufacturerTable
